import { useState } from "react"
import { useNavigate } from "react-router"
import {
    Accordion, AccordionButton, AccordionItem, AccordionPanel,
    Badge, Box, Button, Flex, Input, Progress, Spinner, Text,
    useColorModeValue, useToast
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Tag } from "components"
import { API } from "config"
import { useStockAlerts } from "hooks"

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : null

const exportCsv = (data) => {
    const rows = [
        ['Produto', 'Núcleo', 'Disponível', 'Mínimo', 'Déficit'],
        ...data.flatMap(p =>
            p.nuclei.map(n => [
                p.name,
                n.distributor_name,
                n.available,
                p.min_stock,
                n.deficit,
            ])
        ),
    ]
    const csv  = rows.map(r => r.join(';')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `alertas-estoque-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// ─── Barra de deficit visual ──────────────────────────────────────────────────

const DeficitBar = ({ available, minStock }) => {
    const pct      = minStock > 0 ? Math.round((available / minStock) * 100) : 0
    const scheme   = pct === 0 ? 'red' : pct < 30 ? 'red' : pct < 60 ? 'orange' : 'yellow'
    return (
        <Box>
            <Flex justify="space-between" mb={1}>
                <Text fontSize="2xs" color="gray.400">
                    {available} / {minStock} un.
                </Text>
                <Text fontSize="2xs" fontWeight="bold" color={`${scheme}.500`}>
                    {pct}%
                </Text>
            </Flex>
            <Progress value={pct} max={100} size="xs" colorScheme={scheme}
                borderRadius="full" bg="gray.100" _dark={{ bg: 'gray.700' }} />
        </Box>
    )
}

// ─── Card de núcleo (dentro do accordion) ─────────────────────────────────────

const NucleusCard = ({ nucleus, minStock, productName, productId }) => {
    const navigate   = useNavigate()
    const cardBg     = useColorModeValue('white',    'gray.800')
    const cardBorder = useColorModeValue('gray.100', 'gray.700')
    const labelColor = useColorModeValue('gray.500', 'gray.400')

    const deficitPct = minStock > 0 ? Math.round((nucleus.available / minStock) * 100) : 0
    const isCritical = deficitPct < 30

    return (
        <Box
            bg={cardBg}
            border="1px solid"
            borderColor={isCritical ? 'red.200' : cardBorder}
            borderRadius="xl"
            p={5}
            _dark={{ borderColor: isCritical ? 'red.800' : undefined }}
        >
            {/* Header do card */}
            <Flex justify="space-between" align="flex-start" mb={4} gap={3} flexWrap="wrap">
                <Box>
                    <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
                        {nucleus.distributor_name}
                    </Text>
                    <Flex align="center" gap={1.5} mt={1}>
                        <Box
                            w={2} h={2} borderRadius="full"
                            bg={isCritical ? 'red.400' : 'orange.400'}
                        />
                        <Text fontSize="xs" color={labelColor}>
                            {isCritical ? 'Crítico' : 'Abaixo do mínimo'}
                        </Text>
                    </Flex>
                </Box>
                <Tag
                    colorScheme={isCritical ? 'red' : 'orange'}
                    size="sm"
                    fontWeight="bold"
                >
                    −{nucleus.deficit} un.
                </Tag>
            </Flex>

            {/* Barra de progresso */}
            <DeficitBar available={nucleus.available} minStock={minStock} />

            {/* Ação rápida */}
            <Button
                mt={4} size="xs" w="100%" variant="outline"
                colorScheme="teal" borderRadius="lg"
                leftIcon={<Icon name="edit" boxSize={3} />}
                onClick={() => navigate('/stock/adjustments', {
                    state: { distributorId: nucleus.distributor_id, productId, productName }
                })}
            >
                Ir para Ajuste Manual
            </Button>
        </Box>
    )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Alerts() {
    const toast       = useToast()
    const queryClient = useQueryClient()
    const { data, isFetching, refetch } = useStockAlerts()

    const [search, setSearch]       = useState('')
    const [refreshing, setRefreshing] = useState(false)
    const [lastRefresh, setLastRefresh] = useState(null)

    const allProducts = data?.data ?? []
    const totalCount  = data?.count ?? 0

    const filtered = search.trim()
        ? allProducts
            .map(p => ({
                ...p,
                nuclei: p.nuclei.filter(n =>
                    n.distributor_name.toLowerCase().includes(search.toLowerCase()) ||
                    p.name.toLowerCase().includes(search.toLowerCase())
                ),
            }))
            .filter(p => p.nuclei.length > 0)
        : allProducts

    const cardBg      = useColorModeValue('white',    'gray.800')
    const cardBorder  = useColorModeValue('gray.200', 'gray.700')
    const filterBg    = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor  = useColorModeValue('gray.500', 'gray.400')
    const accordBg    = useColorModeValue('gray.50',  'gray.750')
    const accordBorder = useColorModeValue('gray.100', 'gray.700')

    const handleRefresh = async () => {
        setRefreshing(true)
        try {
            const res = await API.post('/stock/alerts/refresh')
            setLastRefresh(res.data.refreshed_at)
            await queryClient.invalidateQueries({ queryKey: ['stock-alerts'] })
            await refetch()
            toast({
                title: 'Alertas atualizados',
                description: `${res.data.count} produto(s) abaixo do mínimo`,
                status: 'success',
                duration: 4000,
                isClosable: true,
            })
        } catch {
            toast({ title: 'Erro ao atualizar alertas', status: 'error', isClosable: true })
        } finally {
            setRefreshing(false)
        }
    }

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Alertas de Estoque Mínimo">
                <Flex gap={2} align="center">
                    <Button
                        size="sm" colorScheme="orange" variant="outline"
                        leftIcon={<Icon name="refresh" boxSize={3.5} />}
                        isLoading={refreshing}
                        loadingText="Atualizando..."
                        onClick={handleRefresh}
                    >
                        Atualizar agora
                    </Button>
                    <Button
                        size="sm" colorScheme="teal" variant="outline"
                        leftIcon={<Icon name="download" boxSize={3.5} />}
                        isDisabled={allProducts.length === 0}
                        onClick={() => exportCsv(allProducts)}
                    >
                        Exportar CSV
                    </Button>
                    <IconButton size="sm" icon="refresh" isLoading={isFetching} onClick={refetch} />
                </Flex>
            </Heading>

            <Text color={labelColor} fontSize="sm" mt={1} mb={6}>
                Produtos cujo estoque disponível está abaixo do mínimo configurado, por núcleo.
                Cache atualizado automaticamente a cada 4 horas.
            </Text>

            {/* Sumário + última atualização */}
            <Flex gap={4} mb={6} flexWrap="wrap">
                <Box
                    bg={cardBg} border="1px solid" borderColor={cardBorder}
                    borderRadius="xl" px={5} py={4} minW="200px"
                >
                    <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="wider" color={labelColor} mb={1}>
                        Produtos em alerta
                    </Text>
                    <Flex align="baseline" gap={2}>
                        <Text fontSize="3xl" fontWeight="bold" color="orange.500"
                            lineHeight="1">{totalCount}</Text>
                        <Text fontSize="sm" color={labelColor}>produto(s)</Text>
                    </Flex>
                </Box>

                <Box
                    bg={cardBg} border="1px solid" borderColor={cardBorder}
                    borderRadius="xl" px={5} py={4} minW="200px"
                >
                    <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="wider" color={labelColor} mb={1}>
                        Núcleos afetados
                    </Text>
                    <Flex align="baseline" gap={2}>
                        <Text fontSize="3xl" fontWeight="bold" color="red.500"
                            lineHeight="1">
                            {allProducts.reduce((acc, p) => acc + p.nuclei.length, 0)}
                        </Text>
                        <Text fontSize="sm" color={labelColor}>núcleo(s)</Text>
                    </Flex>
                </Box>

                {(lastRefresh) && (
                    <Box
                        bg={cardBg} border="1px solid" borderColor={cardBorder}
                        borderRadius="xl" px={5} py={4}
                    >
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Última atualização manual
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="teal.500">
                            {fmtDate(lastRefresh)}
                        </Text>
                    </Box>
                )}
            </Flex>

            {/* Filtro de busca */}
            <Box
                bg={filterBg} border="1px solid" borderColor={filterBorder}
                borderRadius="xl" p={4} mb={5}
            >
                <Flex align="center" gap={3}>
                    <Icon name="search" boxSize={4} color={labelColor} flexShrink={0} />
                    <Input
                        variant="unstyled"
                        placeholder="Filtrar por produto ou núcleo..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        fontSize="sm"
                    />
                    {search && (
                        <Box
                            as="button"
                            onClick={() => setSearch('')}
                            color={labelColor}
                            _hover={{ color: 'red.400' }}
                            transition="color 0.15s"
                        >
                            <Icon name="cancel" boxSize={4} />
                        </Box>
                    )}
                </Flex>
            </Box>

            {/* Conteúdo */}
            {isFetching && !data ? (
                <Flex justify="center" py={20}><Spinner size="lg" color="orange.400" /></Flex>
            ) : allProducts.length === 0 ? (
                <Flex
                    direction="column" align="center" justify="center"
                    gap={4} py={20}
                    bg={cardBg} border="1px solid" borderColor={cardBorder}
                    borderRadius="2xl"
                >
                    <Box
                        w={16} h={16} borderRadius="2xl"
                        bg="green.50" display="flex" alignItems="center" justifyContent="center"
                        _dark={{ bg: 'green.900' }}
                    >
                        <Icon name="check" boxSize={8} color="green.400" />
                    </Box>
                    <Box textAlign="center">
                        <Text fontWeight="semibold" fontSize="md">Tudo em ordem!</Text>
                        <Text fontSize="sm" color={labelColor} mt={1}>
                            Nenhum produto está abaixo do estoque mínimo configurado.
                        </Text>
                    </Box>
                </Flex>
            ) : filtered.length === 0 ? (
                <Flex direction="column" align="center" gap={2} py={12} color="gray.400">
                    <Icon name="search" boxSize={8} />
                    <Text fontSize="sm">Nenhum resultado para "{search}"</Text>
                </Flex>
            ) : (
                <Accordion allowMultiple defaultIndex={filtered.map((_, i) => i)}>
                    {filtered.map((product) => {
                        const totalDeficit   = product.nuclei.reduce((s, n) => s + n.deficit, 0)
                        const avgPct         = product.nuclei.length > 0
                            ? Math.round(product.nuclei.reduce((s, n) => s + (n.available / product.min_stock) * 100, 0) / product.nuclei.length)
                            : 0
                        const isCritical     = avgPct < 30

                        return (
                            <AccordionItem
                                key={product.id}
                                border="1px solid"
                                borderColor={isCritical ? 'red.200' : cardBorder}
                                borderRadius="2xl"
                                mb={3}
                                overflow="hidden"
                                _dark={{ borderColor: isCritical ? 'red.800' : undefined }}
                                _last={{ mb: 0 }}
                            >
                                {({ isExpanded }) => (
                                    <>
                                        <AccordionButton
                                            px={6} py={4}
                                            bg={isExpanded ? accordBg : cardBg}
                                            _hover={{ bg: accordBg }}
                                            _expanded={{ bg: accordBg }}
                                            borderRadius={isExpanded ? '0' : '2xl'}
                                        >
                                            <Flex flex={1} align="center" gap={4} flexWrap="wrap" textAlign="left">
                                                {/* Ícone de severidade */}
                                                <Flex
                                                    w={9} h={9} borderRadius="xl" flexShrink={0}
                                                    align="center" justify="center"
                                                    bg={isCritical ? 'red.50' : 'orange.50'}
                                                    _dark={{ bg: isCritical ? 'red.900' : 'orange.900' }}
                                                >
                                                    <Icon name="stock" boxSize={4}
                                                        color={isCritical ? 'red.400' : 'orange.400'} />
                                                </Flex>

                                                {/* Nome do produto */}
                                                <Box flex={1} minW="150px">
                                                    <Text fontWeight="semibold" fontSize="sm" lineHeight="short">
                                                        {product.name}
                                                    </Text>
                                                    <Text fontSize="xs" color={labelColor} mt={0.5}>
                                                        Mínimo: {product.min_stock} un.
                                                    </Text>
                                                </Box>

                                                {/* Badges */}
                                                <Flex gap={2} align="center" flexWrap="wrap">
                                                    <Badge
                                                        colorScheme={isCritical ? 'red' : 'orange'}
                                                        borderRadius="full" px={3} py={0.5}
                                                        fontSize="xs" fontWeight="bold"
                                                    >
                                                        −{totalDeficit} un.
                                                    </Badge>
                                                    <Badge
                                                        colorScheme="gray"
                                                        borderRadius="full" px={3} py={0.5}
                                                        fontSize="xs"
                                                    >
                                                        {product.nuclei.length} núcleo{product.nuclei.length !== 1 ? 's' : ''}
                                                    </Badge>
                                                </Flex>
                                            </Flex>

                                            <Box ml={2} color={labelColor}>
                                                <Icon
                                                    name={isExpanded ? 'up' : 'down'}
                                                    boxSize={4}
                                                />
                                            </Box>
                                        </AccordionButton>

                                        <AccordionPanel
                                            px={6} py={5}
                                            bg={accordBg}
                                            borderTop="1px solid"
                                            borderColor={accordBorder}
                                        >
                                            <Box
                                                display="grid"
                                                gridTemplateColumns={{
                                                    base: '1fr',
                                                    sm: 'repeat(2, 1fr)',
                                                    lg: 'repeat(3, 1fr)',
                                                    xl: 'repeat(4, 1fr)',
                                                }}
                                                gap={3}
                                            >
                                                {product.nuclei.map(nucleus => (
                                                    <NucleusCard
                                                        key={nucleus.distributor_id}
                                                        nucleus={nucleus}
                                                        minStock={product.min_stock}
                                                        productName={product.name}
                                                        productId={product.id}
                                                    />
                                                ))}
                                            </Box>
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        )
                    })}
                </Accordion>
            )}
        </Container>
    )
}
